<template>
  <DossierDocumentCard
    title="Controle de Pragas e Vetores"
    :status-options="statusOptions"
    :multiple-status="true"
    :status-requires-validity="['possui_vigente', 'possui_provisorio']"
    :status-requires-upload="['possui_vigente', 'possui_provisorio']"
    :explainer-text="explainerText"
    :mode="mode"
    :not-applicable="false"
    :start-collapsed="startCollapsed"
    v-model="formData"
    save-endpoint="/rest-home/dossier/pest-control"
    upload-endpoint="/rest-home/dossier/pest-control/upload"
    @saved="onSaved"
    @error="onError"
  >
    <template #fields-before>
      <div class="dossier-module__fields">
        <!-- Empresa responsável -->
        <div class="dossier-module__field-group">
          <label class="dossier-module__label">Empresa responsável</label>
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.company_name"
            dense
            outlined
            hide-details
            placeholder="Nome da empresa dedetizadora"
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">{{
            formData.company_name || "—"
          }}</span>
        </div>

        <!-- Data da última aplicação -->
        <div
          class="dossier-module__field-group dossier-module__field-group--small"
        >
          <label class="dossier-module__label">Data da última aplicação</label>
          <v-text-field
            v-if="!isViewMode"
            v-model="formData.last_application_date"
            type="date"
            dense
            outlined
            hide-details
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">
            <i class="fas fa-calendar-alt mr-1"></i>
            {{ formatDateBR(formData.last_application_date) }}
          </span>
        </div>

        <!-- Periodicidade -->
        <div
          class="dossier-module__field-group dossier-module__field-group--small"
        >
          <label class="dossier-module__label">Periodicidade</label>
          <v-select
            v-if="!isViewMode"
            v-model="formData.frequency"
            :items="frequencies"
            dense
            outlined
            hide-details
            placeholder="Selecione"
            class="dossier-module__input"
          />
          <span v-else class="dossier-module__view-value">{{
            formData.frequency || "—"
          }}</span>
        </div>
      </div>

      <!-- Tipos de serviço realizados -->
      <div class="dossier-module__field-group" style="margin-bottom: 14px">
        <label class="dossier-module__label">Tipos de serviço</label>

        <div v-if="!isViewMode" class="dossier-pestcontrol__services">
          <label
            v-for="service in serviceTypes"
            :key="service.value"
            class="dossier-pestcontrol__service-item"
          >
            <input
              type="checkbox"
              :value="service.value"
              v-model="formData.service_types"
              class="dossier-pestcontrol__checkbox"
            />
            <span>{{ service.label }}</span>
          </label>
        </div>

        <div v-else class="dossier-module__view-value">
          {{ displayServiceTypes || "—" }}
        </div>
      </div>
    </template>
  </DossierDocumentCard>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";

const EXPLAINER_TEXT = `
  O controle de pragas e vetores (desinsetização, desratização e descupinização)
  é obrigatório para estabelecimentos de saúde e de longa permanência, conforme
  normas sanitárias vigentes.<br><br>
  O certificado deve ser emitido por empresa especializada devidamente registrada
  e conter: data de execução, pragas controladas, produtos utilizados (com registro
  na ANVISA), responsável técnico e prazo de validade do serviço.<br><br>
  O Sistema Clin não valida o conteúdo do documento, apenas registra sua
  existência para fins de organização e apresentação à fiscalização.
`;

const SERVICE_TYPES = [
  { value: "desinsetizacao", label: "Desinsetização" },
  { value: "desratizacao", label: "Desratização" },
  { value: "descupinizacao", label: "Descupinização" },
  { value: "controle_pombos", label: "Controle de pombos" },
  { value: "outros", label: "Outros" },
];

export default {
  name: "DossierPestControl",
  components: { DossierDocumentCard },
  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },
  data() {
    return {
      explainerText: EXPLAINER_TEXT,
      serviceTypes: SERVICE_TYPES,
      statusOptions: [
        { value: "possui_vigente", label: "Possui certificado vigente" },
        { value: "possui_provisorio", label: "Possui comprovante provisório" },
        { value: "em_elaboracao", label: "Serviço agendado / em execução" },
        { value: "nao_possui", label: "Não possui (pendente)" },
      ],
      frequencies: [
        "Semestral",
        "Trimestral",
        "Anual",
        "Mensal",
        "Conforme necessidade",
      ],
      formData: {
        company_name: "",
        last_application_date: null,
        frequency: null,
        service_types: [],
        status: [],
        validity_date: null,
        file_url: null,
        file_name: null,
        observations: "",
        ...this.flattenInitialData(),
      },
    };
  },
  computed: {
    isViewMode() {
      return this.mode === "view";
    },
    displayServiceTypes() {
      if (!this.formData.service_types?.length) return null;
      return this.formData.service_types
        .map((v) => {
          const found = SERVICE_TYPES.find((s) => s.value === v);
          return found ? found.label : v;
        })
        .join(", ");
    },
  },
  watch: {
    initialData: {
      deep: true,
      handler() {
        Object.assign(this.formData, this.flattenInitialData());
      },
    },
  },
  methods: {
    flattenInitialData() {
      const d = this.initialData || {};
      return {
        company_name: d.company_name || "",
        last_application_date: d.last_application_date || null,
        frequency: d.frequency || null,
        service_types: d.service_types || [],
        status: d.status || [],
        validity_date: d.validity_date || null,
        file_url: d.file_url || null,
        file_name: d.file_name || null,
        observations: d.observations || "",
      };
    },
    onSaved(data) {
      this.$emit("saved", { module: "pest_control", data });
    },
    onError(errors) {
      this.$emit("error", { module: "pest_control", errors });
    },
    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ── Tipos de serviço (checkboxes inline) ──────────────────── */
.dossier-pestcontrol__services {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
}

.dossier-pestcontrol__service-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
}

.dossier-pestcontrol__checkbox {
  accent-color: #1f5a76;
  width: 15px;
  height: 15px;
  cursor: pointer;
}
</style>